import React from 'react';
import { Box } from '@material-ui/core';
import { DataGrid, GridCellParams, GridColumns } from '@material-ui/data-grid';
import { makeStyles } from '@material-ui/styles';
import { CommonRequiringActionWidgetProps } from '../../shared';
import { useCampfireQuery } from '../../../../../../../../global/network/useCampfireQuery';
import { GET_TABLE_WIDGET_BIRTHDAYS } from './birthdays-table-widget-model.gql';
import { GetTableWidgetBirthdays } from './__generated__/GetTableWidgetBirthdays';
import { VolunteerProfile } from '../../../../../../../../common/AvatarName';

const useStyles = makeStyles({
  root: {
    '& .past-birthday': {
      backgroundColor: '#f5f5f5',
    },
  },
});

export const VolunteerBirthdaysTableWidget = (props: CommonRequiringActionWidgetProps) => {
  const classes = useStyles();
  const { filters } = props;

  const { programIds, activityIds } = JSON.parse(filters) as { programIds: [string]; activityIds: [string] };

  const { data, loading } = useCampfireQuery<GetTableWidgetBirthdays, any>(GET_TABLE_WIDGET_BIRTHDAYS, {
    options: {
      variables: {
        programIds: programIds.length > 0 && !programIds.includes('all') ? programIds : null,
        activityIds: activityIds.length > 0 && !activityIds.includes('all') ? activityIds : null,
      },
    },
  });

  const birthdays =
    data?.vm.profileBirthdays
      .map((birthday) => {
        const birthDate = new Date(birthday.dateOfBirth);
        const today = new Date();
        const currentYear = today.getFullYear();

        // Calculate this year's birthday
        const thisYearBirthday = new Date(currentYear, birthDate.getMonth(), birthDate.getDate());

        // Calculate age they're turning this year (regardless of whether it's passed)
        const ageTheyAreTurning = currentYear - birthDate.getFullYear();

        // Check if birthday is today
        const isToday = thisYearBirthday.toDateString() === today.toDateString();

        // Check if this year's birthday has passed
        const hasPassed = thisYearBirthday < today && !isToday;

        return {
          id: birthday.profileId,
          profileId: birthday.profileId,
          preferredName: birthday.preferredName ?? birthday.firstName,
          firstName: birthday.firstName,
          lastName: birthday.lastName,
          dateOfBirth: birthDate,
          thisYearBirthday: thisYearBirthday,
          ageTheyAreTurning: ageTheyAreTurning,
          isToday: isToday,
          hasPassed: hasPassed,
          userId: birthday.userId,
        };
      })
      .sort((a, b) => {
        // Sort by this year's birthday, earliest to latest
        return a.thisYearBirthday.getTime() - b.thisYearBirthday.getTime();
      }) || [];

  const columns: GridColumns = [
    {
      field: 'volunteerName',
      headerName: 'Volunteer',
      flex: 1,
      disableColumnMenu: true,
      minWidth: 180,
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        return (
          <VolunteerProfile
            inTable
            name={`${row.preferredName} ${row.lastName}`}
            lastName={row.firstName}
            profileId={row.profileId}
            preferredName={row.preferredName}
            userId={row.userId}
          />
        );
      },
    },
    {
      field: 'dateOfBirth',
      headerName: 'Date of Birth',
      flex: 1,
      disableColumnMenu: true,
      minWidth: 170,
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        const date = params.value as Date;
        const formattedDate = date.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        });

        return (
          <div>
            {row.isToday && (
              <div
                style={{
                  fontSize: '0.7rem',
                  color: '#666',
                  marginBottom: '1px',
                  lineHeight: '1.2',
                }}
              >
                Today{' '}
                <span role='img' aria-label='celebration'>
                  🎉
                </span>
              </div>
            )}
            <div style={{ lineHeight: '1.3' }}>{formattedDate}</div>
          </div>
        );
      },
    },
    {
      field: 'ageTheyAreTurning',
      headerName: 'Age',
      flex: 1,
      disableColumnMenu: true,
      minWidth: 100,
      renderCell: (params: GridCellParams) => {
        return `${params.value} years old`;
      },
    },
  ];

  return (
    <Box width='100%' height='100%'>
      <DataGrid
        className={classes.root}
        columns={columns}
        rows={birthdays}
        loading={loading}
        autoPageSize
        disableSelectionOnClick
        getRowClassName={(params) => (params.row.hasPassed ? 'past-birthday' : '')}
      />
    </Box>
  );
};
